import { useEffect, useRef, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { LoadingState } from '../components/LoadingState'
import { useAuth } from '../contexts/AuthContext'
import { track } from '../lib/analytics'

/**
 * Did the user arrive here from the Composer's anonymous-submit gate, or by
 * clicking "Sign in" in the header?
 *
 * Presence-check on sessionStorage rather than importing the Composer's
 * constants — keeps LoginPage from depending on Composer internals and matches
 * the same key shape the Composer sets (sessionStorage key
 * 'ponpon:pending_compose', JSON object with 'prompt' / 'aspectRatio' /
 * 'stashedAt' fields, 30-min TTL enforced by the reader).
 *
 * Two callers now: the post-auth redirect below, and the `gate_hit` analytics
 * event, which needs the distinction to tell "typed a prompt and hit the wall"
 * apart from "clicked sign in" — those are different funnel steps and lumping
 * them together makes the drop-off unreadable.
 */
function hasPendingCompose(): boolean {
  try {
    const raw = sessionStorage.getItem('ponpon:pending_compose')
    if (!raw) return false
    const parsed = JSON.parse(raw) as { stashedAt?: number }
    if (!parsed.stashedAt) return false
    // Match Composer's 30-min TTL — a stale stash routes to /dashboard
    // instead of looping the user back to "/" with an effectively-empty
    // pending.
    return Date.now() - parsed.stashedAt <= 30 * 60 * 1000
  } catch {
    return false
  }
}

export function LoginPage() {
  const {
    session, signInWithGoogle, sendSignInLink, completeEmailLinkSignIn, loading,
  } = useAuth()
  const [email, setEmail] = useState('')
  const [linkSent, setLinkSent] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Starts TRUE, because this component unconditionally attempts a magic-link
  // completion on mount — it is genuinely "completing" from the very first
  // render. Initialising to false was a real bug, not a style choice: the
  // effect below calls setCompletingLink(true), but that schedules a re-render
  // and does NOT change what the gate_hit effect reads in the same commit. So
  // gate_hit fired on the first commit for every magic-link return, which is
  // exactly the over-count the effect was rewritten to avoid.
  // It also removes a one-frame flash of the sign-in form before the skeleton.
  const [completingLink, setCompletingLink] = useState(true)

  // The /login route is the magic-link redirect target. If the user
  // landed here by clicking a sign-in email, finish the sign-in: ask
  // AuthContext to run isSignInWithEmailLink + signInWithEmailLink
  // against the current URL. On success Firebase fires onAuthStateChanged
  // → session populates → the <Navigate to="/dashboard" /> branch below
  // takes over. Errors surface in the same banner as the request side.
  useEffect(() => {
    let cancelled = false
    setCompletingLink(true)
    completeEmailLinkSignIn()
      .then((ok) => {
        if (cancelled) return
        if (ok) {
          // Strip the Firebase oobCode query string so a refresh of
          // /dashboard doesn't try to re-redeem an already-used link.
          window.history.replaceState({}, '', '/login')
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'Sign-in link failed')
      })
      .finally(() => {
        if (!cancelled) setCompletingLink(false)
      })
    return () => { cancelled = true }
  }, [completeEmailLinkSignIn])

  // The gate: someone was actually asked to sign in.
  //
  // Deliberately NOT mount-only. On mount `session` is always null and
  // `completeEmailLinkSignIn` is still in flight, so a mount-only version fires
  // for every magic-link return too — inflating the wall-hit count with the
  // people who already cleared it, which is the one number this event exists to
  // measure. Wait until the page has settled and is genuinely rendering the
  // sign-in form, then fire once.
  const gateTracked = useRef(false)
  useEffect(() => {
    if (loading || completingLink || session) return
    if (gateTracked.current) return
    gateTracked.current = true
    track('gate_hit', { from_composer: hasPendingCompose() })
  }, [loading, completingLink, session])

  if (loading || completingLink) {
    return <LoadingState label="Preparing sign-in" />
  }
  if (session) {
    // If the user got here from the Composer (anonymous submit
    // stashed their prompt and bounced to /login), route them BACK
    // to "/" so the Composer's auto-resume effect can pick up the
    // pending stash and fire createProject. Sending them to
    // /dashboard would silently drop the prompt and force a retype.
    //
    // See hasPendingCompose above for why this is a presence-check rather
    // than an import of the Composer's constants.
    return <Navigate to={hasPendingCompose() ? '/' : '/dashboard'} replace />
  }

  return (
    <section className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-12 pb-24">
      <div className="grid grid-cols-12 gap-10 items-stretch min-h-[78vh]">
        {/* Pitch side */}
        <div className="col-span-12 lg:col-span-7 flex flex-col justify-center">
          <h1 className="h-display text-[clamp(2.8rem,6.8vw,5.8rem)] text-text">
            Step into the <span className="text-accent">booth</span>.
          </h1>
          <p className="mt-6 text-[1.15rem] text-text-2 max-w-lg leading-relaxed">
            Same account as the haevi console. Your productions, cast, and drafts follow you in.
          </p>

          <div className="hidden lg:grid grid-cols-3 gap-6 mt-12 max-w-md">
            <Step n="01" label="Sign in" active />
            <Step n="02" label="Describe" />
            <Step n="03" label="Direct" />
          </div>
        </div>

        {/* Auth card */}
        <div className="col-span-12 lg:col-span-5 flex items-center">
          <div className="glass rounded-lg shadow-lg w-full">
            <div className="px-7 py-6 border-b border-line">
              <h2
                className="text-[1.6rem] text-text leading-tight"
                style={{ fontVariationSettings: '"wdth" 96, "opsz" 96', fontWeight: 600, letterSpacing: '-0.022em' }}
              >
                Sign in
              </h2>
              <p className="text-text-3 text-[0.86rem] mt-1">
                Free preview. Same account as Console.
              </p>
            </div>

            <div className="p-7 space-y-4">
              <button
                onClick={async () => {
                  // Before the call: on mobile this is signInWithRedirect, so
                  // the page is gone a tick later and anything tracked after
                  // the await never sends.
                  track('signin_started', { method: 'google' })
                  try { await signInWithGoogle() }
                  catch (err: unknown) {
                    setError(err instanceof Error ? err.message : 'Sign-in failed')
                  }
                }}
                className="btn-accent w-full justify-center"
              >
                <GoogleIcon />
                <span>Continue with Google</span>
              </button>

              <div className="flex items-center gap-3 text-text-3 text-[0.78rem]">
                <span className="flex-1 h-px bg-line-2" />
                <span>or by email</span>
                <span className="flex-1 h-px bg-line-2" />
              </div>

              <form
                onSubmit={async (e) => {
                  e.preventDefault()
                  setError(null)
                  track('signin_started', { method: 'email_link' })
                  try { await sendSignInLink(email); setLinkSent(true) }
                  catch (err: unknown) {
                    setError(err instanceof Error ? err.message : 'Send failed')
                  }
                }}
                className="space-y-2.5"
              >
                <label className="block">
                  <span className="block text-text-3 text-[0.78rem] mb-1.5">Email</span>
                  <input
                    type="email"
                    required
                    placeholder="you@studio.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="field"
                  />
                </label>
                <button type="submit" className="btn-ghost w-full justify-center">
                  Send sign-in link →
                </button>
              </form>

              {linkSent && (
                <p className="text-ok text-[0.86rem]">✓ Check your inbox for the link.</p>
              )}
              {error && (
                <p className="text-err text-[0.86rem]">⨯ {error}</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

function Step({ n, label, active }: { n: string; label: string; active?: boolean }) {
  return (
    <div>
      <div className={`t-mono text-[0.86rem] mb-1 ${active ? 'text-accent' : 'text-text-3'}`}>{n}</div>
      <div className={active ? 'text-text' : 'text-text-2'}>{label}</div>
    </div>
  )
}

function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden>
      <path fill="#0A0A05" d="M24 9.5c3.3 0 6.3 1.1 8.7 3.3l6.5-6.5C35.2 2.5 30 .5 24 .5 14.7.5 6.7 5.9 2.9 13.7l7.6 5.9C12.4 13.7 17.7 9.5 24 9.5z"/>
      <path fill="#0A0A05" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.9 7.2l7.6 5.9c4.4-4.1 7.1-10.2 7.1-17.6z"/>
      <path fill="#0A0A05" d="M10.5 28.4c-.5-1.4-.8-2.9-.8-4.4s.3-3 .8-4.4l-7.6-5.9C1.2 17 .5 20.4.5 24s.7 7 1.9 10.3l8.1-5.9z"/>
      <path fill="#0A0A05" d="M24 47.5c6 0 11.1-2 14.8-5.4l-7.6-5.9c-2.1 1.4-4.8 2.3-7.2 2.3-6.3 0-11.6-4.2-13.5-9.9l-7.6 5.9C6.7 42.1 14.7 47.5 24 47.5z"/>
    </svg>
  )
}
